// src/pages/packages/ModalPackageConfiguration.jsx
import "./ModalPackageConfiguration.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Modal, ProgressBar } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";

import AppLoading from "../../../../commons/AppLoading";
import HallAppToast from "../../../../commons/HallAppToast";
import PackageAPI from "../api/PackageAPI";
import PackageStep1 from "./steps/PackageStep1";
import PackageStep2 from "./steps/PackageStep2";
import PackageStep3 from "./steps/PackageStep3";

const { Formik } = formik;
const packageAPI = new PackageAPI();

function StepDots({ step, total }) {
  return (
    <div className="d-flex align-items-center gap-2 w-100">
      {Array.from({ length: total }).map((_, idx) => {
        const i = idx + 1;
        const active = i <= step;

        return (
          <div key={i} className="d-flex align-items-center flex-grow-1">
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: active ? "#2f2a7a" : "#e7e7f4",
                border: active ? "1px solid #2f2a7a" : "1px solid #d8d8ee",
              }}
              aria-label={`Passo ${i}`}
            />
            {i !== total && (
              <div
                style={{
                  height: 2,
                  flex: 1,
                  marginLeft: 8,
                  marginRight: 8,
                  background: i < step ? "#2f2a7a" : "#e7e7f4",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ModalPackageConfiguration({ show, closeShow, packageId, onSuccess }) {
  const TOTAL_STEPS = 3;
  const editing = !!packageId;

  const formikRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const progress = useMemo(() => Math.round((step / TOTAL_STEPS) * 100), [step]);

  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return "Informações";
      case 2:
        return "Itens";
      case 3:
        return "Preço";
      default:
        return "Pacotes";
    }
  }, [step]);

  const schemaStep1 = useMemo(() => {
    return yup.object({
      name: yup
        .string()
        .trim()
        .min(3, "Mínimo de 3 caracteres")
        .required("Nome do pacote é obrigatório"),

      description: yup.string().max(500, "Máx. 500 caracteres").nullable(),

      status: yup
        .mixed()
        .oneOf(["ACTIVE", "INACTIVE"], "Status inválido")
        .required("Status é obrigatório"),

      validityType: yup
        .mixed()
        .oneOf(["NONE", "DATE"], "Validade inválida")
        .required("Validade é obrigatória"),

      expiryDate: yup.string().when("validityType", {
        is: "DATE",
        then: (s) =>
          s
            .required("Selecione a data de validade")
            .matches(/^\d{4}-\d{2}-\d{2}$/, "Data inválida"),
        otherwise: (s) => s.notRequired().nullable(true),
      }),
    });
  }, []);

  const schemaGlobal = useMemo(() => schemaStep1, [schemaStep1]);

  const initialValues = useMemo(
    () => ({
      name: "",
      description: "",
      status: "ACTIVE",
      validityType: "NONE",
      expiryDate: "",

      itemsType: "SERVICE_QUANTITY",
      items: [],

      priceAmount: 0,
      allowDiscount: false,
      discountType: null,
      maxDiscountPercent: null,
      maxDiscountAmount: null,
    }),
    []
  );

  const mapApiToForm = (data) => {
    const items = Array.isArray(data?.items)
      ? data.items.map((it) => ({
          serviceId: it.serviceId,
          serviceName: it.serviceName || "",
          quantity: Number(it.quantity || 1),
        }))
      : [];

    const itemsType = items.length > 1 ? "COMBO_SERVICES" : "SERVICE_QUANTITY";

    return {
      name: data?.name || "",
      description: data?.description || "",
      status: data?.status || "ACTIVE",
      validityType: data?.expiryDate ? "DATE" : "NONE",
      expiryDate: data?.expiryDate || "",

      itemsType,
      items,

      priceAmount: Number(data?.priceAmount || 0),
      allowDiscount: !!data?.allowDiscount,
      discountType: data?.discountType || null,
      maxDiscountPercent: data?.maxDiscountPercent ?? null,
      maxDiscountAmount: data?.maxDiscountAmount ?? null,
    };
  };

  useEffect(() => {
    if (!show) return;

    setStep(1);

    const form = formikRef.current;
    if (form) {
      form.setValues(initialValues, false);
      form.setErrors({});
      form.setTouched({});
      form.setSubmitting(false);
    }

    const loadEdit = async () => {
      if (!packageId) return;

      try {
        setLoading(true);

        const resp = await packageAPI.findById(packageId);
        const data = resp?.data || resp;

        const nextValues = mapApiToForm(data);

        if (formikRef.current) {
          formikRef.current.setValues(nextValues, false);
          formikRef.current.setErrors({});
          formikRef.current.setTouched({});
        }
      } catch (e) {
        console.error("ERRO AO CARREGAR PACOTE:", e);
        HallAppToast.genericError();
      } finally {
        setLoading(false);
      }
    };

    loadEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, packageId]);

  const close = () => {
    setStep(1);
    closeShow?.();
  };

  async function validateAndGoNext(f) {
    try {
      if (step === 1) {
        await schemaStep1.validate(f.values, { abortEarly: false });

        f.setTouched({
          ...f.touched,
          name: true,
          description: true,
          status: true,
          validityType: true,
          expiryDate: true,
        });

        setStep(2);
        return;
      }

      setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    } catch (err) {
      const nextErrors = {};

      if (err?.inner?.length) {
        err.inner.forEach((e) => {
          if (e?.path) nextErrors[e.path] = e.message;
        });
      } else if (err?.path) {
        nextErrors[err.path] = err.message;
      }

      f.setErrors({ ...f.errors, ...nextErrors });

      if (step === 1) {
        f.setTouched({
          ...f.touched,
          name: true,
          description: true,
          status: true,
          validityType: true,
          expiryDate: true,
        });
      }

      HallAppToast.error("Revise os campos obrigatórios para continuar.");
    }
  }

  async function onSubmit(values, actions) {
    try {
      setLoading(true);

      if (editing) {
        await packageAPI.update(packageId, values);
      } else {
        await packageAPI.create(values);
      }

      HallAppToast.success(editing ? "Pacote atualizado!" : "Pacote salvo!");
      onSuccess?.();
      close();
    } catch (e) {
      console.error("ERRO AO SALVAR PACOTE:", e);
      HallAppToast.genericError();
    } finally {
      actions?.setSubmitting(false);
      setLoading(false);
    }
  }

  return (
    <Modal
      show={show}
      fullscreen={true}
      centered
      size="lg"
      onHide={close}
      contentClassName="hallapp-modal hallapp-modal-package"
    >
      <Modal.Header closeButton className="border-bottom">
        <div className="w-100">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div style={{ minWidth: 0 }}>
              <div className="hallapp-modal-package__title">
                {editing ? "Editar Pacote" : "Criar Pacote"}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {stepTitle} • Passo {step} de {TOTAL_STEPS}
              </div>
            </div>
          </div>

          <div className="mt-3">
            <StepDots step={step} total={TOTAL_STEPS} />
            <div className="mt-2">
              <ProgressBar now={progress} style={{ height: 6, borderRadius: 999 }} />
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="container position-relative">
          {loading && (
            <div className="hallapp-loading-overlay">
              <AppLoading />
            </div>
          )}

          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={schemaGlobal}
            validateOnChange={false}
            validateOnBlur={true}
            onSubmit={onSubmit}
          >
            {(f) => (
              <>
                {step === 1 && (
                  <PackageStep1
                    formik={f}
                    next={() => validateAndGoNext(f)}
                  />
                )}

                {step === 2 && (
                  <PackageStep2
                    formik={f}
                    next={() => setStep(3)}
                    toBack={() => setStep(1)}
                  />
                )}

                {step === 3 && (
                  <PackageStep3
                    formik={f}
                    toBack={() => setStep(2)}
                    onSave={() => f.handleSubmit()}
                  />
                )}
              </>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ModalPackageConfiguration;